import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Upload, BarChart3, Loader2, FileText, TrendingUp,
  Quote, Users, Award, Filter,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CJ_AVATAR_URL } from "../lib/constants";
import { toast } from "sonner";

export default function Impact() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [trackRecord, setTrackRecord] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [filterProgram, setFilterProgram] = useState("All");
  const [filterClient, setFilterClient] = useState("All");
  const [sort, setSort] = useState("recent");

  const loadAll = () => {
    setLoading(true);
    return Promise.all([
      api.get("/impact/track-record").then(({ data }) => setTrackRecord(data?.cards || [])).catch(() => setTrackRecord([])),
      api.get("/impact/programs").then(({ data }) => setPrograms(data?.programs || [])).catch(() => setPrograms([])),
      api.get("/impact/clients").then(({ data }) => setClients(data?.clients || [])).catch(() => setClients([])),
    ]).finally(() => setLoading(false));
  };

  const loadReports = () => {
    const params = {};
    if (filterProgram !== "All") params.program = filterProgram;
    if (filterClient !== "All") params.client = filterClient;
    if (sort) params.sort = sort;
    api.get("/impact/reports", { params })
      .then(({ data }) => setReports(Array.isArray(data) ? data : []))
      .catch(() => setReports([]));
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadReports(); /* eslint-disable-next-line */ }, [filterProgram, filterClient, sort]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.includes("pdf")) { toast.error("Please upload a PDF file."); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("PDF is over 20 MB."); return; }
    setUploading(true);
    const tId = toast.loading("CJ is reading your report...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/impact/reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report parsed and saved.", { id: tId });
      await loadAll();
      loadReports();
      navigate(`/impact/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse this PDF.", { id: tId });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isAdmin = user?.role === "super_admin";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <Link to="/" data-testid="impact-back" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink">
        <ArrowLeft size={14} /> Back to Hub
      </Link>

      <div className="mt-4 flex items-start gap-4 sm:gap-5 flex-wrap">
        <img src={CJ_AVATAR_URL} alt="CJ" className="h-16 w-16 rounded-full object-cover shadow-md ring-2 ring-white shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <div className="tc-eyebrow text-green-600">Training Impact</div>
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">Real numbers from real programs.</h1>
          <p className="text-slate-500 mt-1.5 max-w-xl text-sm">Program-level track record up top. Drill into any client report below.</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="impact-upload-button"
            className="h-12 px-5 rounded-2xl text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60"
            style={{ background: "#7ac143" }}
          >
            {uploading ? <><Loader2 size={16} className="animate-spin" /> Parsing...</> : <><Upload size={16} /> Upload PDF</>}
          </button>
        )}
        <input ref={fileInputRef} type="file" accept="application/pdf" onChange={(e) => handleUpload(e.target.files?.[0])} className="hidden" data-testid="impact-file-input" />
      </div>

      {/* Track-record cards */}
      <section className="mt-8" data-testid="impact-track-record">
        <div className="flex items-center gap-2 mb-3">
          <Award size={16} className="text-green-600" />
          <h2 className="font-display text-xl text-tc-ink">Program track record</h2>
        </div>
        {loading ? (
          <div className="text-slate-400 py-8 text-center">Loading…</div>
        ) : trackRecord.length === 0 ? (
          <div className="text-slate-500 py-8 text-center text-sm">No program data yet. Upload an impact PDF to seed this.</div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {trackRecord.map((c, i) => <TrackRecordCard key={c.program_name} c={c} delay={i * 0.05} />)}
          </div>
        )}
      </section>

      {/* Client reports */}
      <section className="mt-10">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-tc-ink" />
            <h2 className="font-display text-xl text-tc-ink">Client reports</h2>
            <span className="text-xs text-slate-400">({reports.length})</span>
          </div>
          <Filters
            programs={programs}
            clients={clients}
            program={filterProgram} setProgram={setFilterProgram}
            client={filterClient} setClient={setFilterClient}
            sort={sort} setSort={setSort}
          />
        </div>

        {reports.length === 0 ? (
          <div className="tc-card p-10 text-center text-slate-500" data-testid="impact-empty">
            <BarChart3 size={36} className="mx-auto text-slate-300" />
            <div className="font-display text-2xl text-tc-ink mt-3">No reports match those filters.</div>
            <div className="text-sm mt-1">{isAdmin ? "Upload a PDF or change your filters." : "Once your team uploads reports, you'll see them here."}</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="impact-grid">
            {reports.map((r, i) => <ReportCard key={r.id} r={r} delay={i * 0.03} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function Filters({ programs, clients, program, setProgram, client, setClient, sort, setSort }) {
  const SELECT = "h-10 px-3 rounded-xl bg-white border-2 border-slate-200 focus:border-green-500 outline-none text-xs sm:text-sm font-bold";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="impact-filters">
      <div className="flex items-center gap-1 text-xs text-slate-400 font-bold uppercase tracking-widest">
        <Filter size={12} /> Filter:
      </div>
      <select value={program} onChange={(e) => setProgram(e.target.value)} data-testid="impact-filter-program" className={SELECT}>
        <option value="All">All programs</option>
        {programs.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select value={client} onChange={(e) => setClient(e.target.value)} data-testid="impact-filter-client" className={SELECT}>
        <option value="All">All clients</option>
        {clients.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="impact-filter-sort" className={SELECT}>
        <option value="recent">Most recent</option>
        <option value="improvement">Biggest % gain</option>
      </select>
    </div>
  );
}

function TrackRecordCard({ c, delay }) {
  const isVirtual = c.is_virtual;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
      className="rounded-2xl bg-[#083050] text-white p-6 overflow-hidden shadow-lg"
      data-testid={`track-record-${c.program_name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#ffd400]">The Results</div>
          <h3 className="font-display text-2xl sm:text-3xl leading-tight mt-1">{c.program_name}</h3>
        </div>
        {!isVirtual && (
          <div className="text-right text-[11px] text-slate-300">
            {c.report_count > 0 && <div>{c.report_count} {c.report_count === 1 ? "report" : "reports"}</div>}
            {c.client_count > 0 && <div>{c.client_count} {c.client_count === 1 ? "client" : "clients"}</div>}
            {c.participant_count > 0 && <div>{c.participant_count} participants</div>}
          </div>
        )}
      </div>

      {/* Headline stats — for virtual cards (Connected Leadership) */}
      {(c.headline_stats || []).length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-3">
          {c.headline_stats.map((s, i) => (
            <div key={i} className="rounded-xl bg-white/5 p-4 border border-white/10">
              <div className="font-display text-3xl sm:text-4xl leading-none" style={{ color: s.color }}>{s.value}</div>
              <div className="text-[11px] sm:text-xs text-slate-300 mt-2 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overall % improvement — for live data */}
      {c.avg_percent_improvement !== null && c.avg_percent_improvement !== undefined && (
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-white/5 p-4 border border-white/10">
            <div className="font-display text-4xl leading-none text-[#7ac143]">+{c.avg_percent_improvement}%</div>
            <div className="text-xs text-slate-300 mt-2 leading-snug">Average overall improvement across every report</div>
          </div>
          {(c.top_categories || [])[0] && (
            <div className="rounded-xl bg-white/5 p-4 border border-white/10">
              <div className="font-display text-4xl leading-none text-[#f68b1f]">+{c.top_categories[0].avg_percent_improvement}%</div>
              <div className="text-xs text-slate-300 mt-2 leading-snug">Top category: <span className="font-bold text-white">{c.top_categories[0].name}</span></div>
            </div>
          )}
        </div>
      )}

      {/* Top categories list (live data) */}
      {(c.top_categories || []).length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#ffd400] mb-2">Where teams grew most</div>
          <div className="space-y-1.5">
            {c.top_categories.slice(0, 4).map((cat) => (
              <div key={cat.name} className="flex items-center gap-3">
                <div className="flex-1 text-sm">{cat.name}</div>
                <div className="font-display text-base text-[#7ac143]">+{cat.avg_percent_improvement}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Before/After block (virtual data) */}
      {(c.before_after || []).map((ba, i) => (
        <div key={i} className="mt-5">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-300 mb-2">{ba.label}: Before & After</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-white/5 p-4 border border-white/10">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Before the program</div>
              <div className="font-display text-3xl mt-1">{ba.before_pct}%</div>
              <div className="text-[11px] text-slate-300 mt-1.5 leading-snug">of leaders rated themselves at the top two confidence levels</div>
            </div>
            <div className="rounded-xl bg-[#7ac143]/15 p-4 border-2 border-[#7ac143]/60">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7ac143]">After the program</div>
              <div className="font-display text-3xl mt-1 text-[#7ac143]">{ba.after_pct}%</div>
              <div className="text-[11px] text-slate-300 mt-1.5 leading-snug">{ba.note}</div>
            </div>
          </div>
        </div>
      ))}

      {/* Featured reflections */}
      {(c.featured_reflections || []).length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <Quote size={14} className="text-[#ffd400]" />
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#ffd400]">Participants said</div>
          </div>
          <div className="space-y-2.5">
            {c.featured_reflections.slice(0, 5).map((r, i) => (
              <blockquote key={i} className="text-sm text-slate-200 italic leading-snug pl-3 border-l-2 border-[#ffd400]/50">
                "{r}"
              </blockquote>
            ))}
          </div>
        </div>
      )}

      {c.source_note && (
        <div className="mt-4 text-[10px] italic text-slate-400">{c.source_note}</div>
      )}
    </motion.article>
  );
}

function ReportCard({ r, delay }) {
  const pct = typeof r.overall_percent_improvement === "number" ? r.overall_percent_improvement : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
    >
      <Link to={`/impact/${r.id}`} data-testid={`impact-card-${r.id}`} className="tc-card p-6 block hover:-translate-y-1 transition-all group">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-green-700">
            <FileText size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-400">Program</div>
            <div className="font-bold text-sm text-tc-ink truncate">{r.canonical_program || r.program_name}</div>
          </div>
        </div>
        {r.client_name && (
          <div className="mt-4">
            <div className="tc-eyebrow text-slate-400">Client</div>
            <div className="font-display text-xl text-tc-ink mt-0.5">{r.client_name}</div>
          </div>
        )}
        {pct !== null && (
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-widest">
              <TrendingUp size={14} className="text-green-600" /> Improvement
            </span>
            <span className="font-display text-3xl text-green-600 leading-none">{pct.toFixed(1)}%</span>
          </div>
        )}
      </Link>
    </motion.div>
  );
}
